/**
 * MapRenderer — Renders the minimap for both dungeon and overworld modes.
 * Features: smooth rotation (player always faces up), zoom (wheel + pinch),
 * offscreen static tile cache, triangle arrows for all entities.
 */
export default class MapRenderer {
  constructor() {
    // External references (set by caller)
    this.player = null;
    this.world = null;
    this.playerSync = null;
    this.lootManager = null;
    this.dungeonGenerator = null;
    this.orbitalCamera = null;   // for camera-based rotation
    this.isDungeonMode = false;

    // Canvas
    this.canvas = null;
    this.ctx = null;

    // Zoom state
    this.zoomLevel = 3;          // dungeon: px per tile
    this.minZoom = 1;
    this.maxZoom = 12;
    this.overworldZoom = 1;      // overworld multiplier
    this.minOverworldZoom = 0.3;
    this.maxOverworldZoom = 5;

    // Smooth position + instant angles
    this._smoothX = 0;
    this._smoothZ = 0;
    this._cameraAngle = 0;   // from orbitalCamera, instant
    this._playerFacing = 0;  // player facingAngle, for arrow only
    this._initialized = false;

    // Offscreen static dungeon canvas
    this._staticCanvas = null;
    this._staticCtx = null;
    this._lastLevelIndex = -1;
    this._lastDungeonSeed = -1;

    // Pinch-to-zoom
    this._activeTouches = new Map();
    this._pinchStartDist = 0;
    this._pinchStartZoom = 0;

    // Bound handlers
    this._onWheel = this._handleWheel.bind(this);
    this._onPointerDown = this._handlePointerDown.bind(this);
    this._onPointerMove = this._handlePointerMove.bind(this);
    this._onPointerUp = this._handlePointerUp.bind(this);
  }

  // ── Canvas lifecycle ──────────────────────────────────────────────

  attachToCanvas(canvas) {
    if (this.canvas === canvas) return;
    this.detach();
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    canvas.style.touchAction = 'none';
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
    canvas.addEventListener('pointerdown', this._onPointerDown);
    canvas.addEventListener('pointermove', this._onPointerMove);
    canvas.addEventListener('pointerup', this._onPointerUp);
    canvas.addEventListener('pointercancel', this._onPointerUp);
  }

  detach() {
    if (!this.canvas) return;
    this.canvas.removeEventListener('wheel', this._onWheel);
    this.canvas.removeEventListener('pointerdown', this._onPointerDown);
    this.canvas.removeEventListener('pointermove', this._onPointerMove);
    this.canvas.removeEventListener('pointerup', this._onPointerUp);
    this.canvas.removeEventListener('pointercancel', this._onPointerUp);
    this.canvas = null;
    this.ctx = null;
    this._activeTouches.clear();
  }

  // ── Main update (called every frame) ──────────────────────────────

  update(dt) {
    if (!this.player || !this.canvas || !this.ctx) return;

    // Smooth position interpolation (no angle lerp — rotation is instant from camera)
    const lerpF = 1 - Math.pow(0.001, dt);
    const px = this.player.x;
    const pz = this.player.z;

    if (!this._initialized) {
      this._smoothX = px;
      this._smoothZ = pz;
      this._initialized = true;
    } else {
      this._smoothX += (px - this._smoothX) * lerpF;
      this._smoothZ += (pz - this._smoothZ) * lerpF;
    }

    // Camera angle drives map rotation (instant, no lerp)
    // OrbitalCameraController.currentOrbitAngle is in degrees
    this._cameraAngle = -(this.orbitalCamera?.currentOrbitAngle || 0) * Math.PI / 180;

    // Player facing angle (for the player arrow only)
    this._playerFacing = (this.player.facingAngle || 0) * Math.PI / 180;

    // Render
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (w <= 0 || h <= 0) return;

    if (this.isDungeonMode && this.dungeonGenerator) {
      const level = this.dungeonGenerator.getCurrentLevel();
      if (level?.grid) this._renderDungeon(w, h, level);
    } else {
      this._renderOverworld(w, h);
    }
  }

  // ── Offscreen static dungeon tiles ────────────────────────────────

  _rebuildStatic(level) {
    const gw = level.gridWidth;
    const gh = level.gridHeight;
    if (!this._staticCanvas) {
      this._staticCanvas = document.createElement('canvas');
      this._staticCtx = this._staticCanvas.getContext('2d');
    }
    this._staticCanvas.width = gw;
    this._staticCanvas.height = gh;
    const sc = this._staticCtx;
    sc.clearRect(0, 0, gw, gh);

    // Grid tiles
    for (let x = 0; x < gw; x++) {
      for (let z = 0; z < gh; z++) {
        const t = level.grid[x]?.[z];
        if (t === 1) { sc.fillStyle = '#334155'; sc.fillRect(x, z, 1, 1); }
        else if (t === 2) { sc.fillStyle = '#64748b'; sc.fillRect(x, z, 1, 1); }
      }
    }

    // Special cuboid features
    if (level.cuboidDefs) {
      for (const def of level.cuboidDefs) {
        switch (def.role) {
          case 'pillar':      sc.fillStyle = '#94a3b8'; break;
          case 'portal':      sc.fillStyle = '#a855f7'; break;
          case 'back-portal': sc.fillStyle = '#06b6d4'; break;
          case 'chest':       sc.fillStyle = '#f59e0b'; break;
          default: continue;
        }
        sc.fillRect(
          Math.floor(def.x), Math.floor(def.z),
          Math.max(1, Math.floor(def.width)), Math.max(1, Math.floor(def.depth))
        );
      }
    }

    this._lastLevelIndex = level.levelIndex ?? -1;
  }

  // ── Dungeon rendering ─────────────────────────────────────────────

  _renderDungeon(w, h, level) {
    const ctx = this.ctx;
    const zoom = this.zoomLevel;

    // Rebuild static canvas on level change or new dungeon run
    const li = level.levelIndex ?? 0;
    const seed = this.dungeonGenerator?.seed ?? 0;
    if (li !== this._lastLevelIndex || seed !== this._lastDungeonSeed) {
      this._lastDungeonSeed = seed;
      this._rebuildStatic(level);
    }

    // Map rotation: camera angle + PI, with X-flip to match screen layout
    // At cam=0 (looking +Z): +Z→UP, +X→RIGHT on map
    const rotation = this._cameraAngle + Math.PI;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(5, 10, 20, 0.85)';
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-rotation);
    ctx.scale(-zoom, zoom);  // flip X to match game screen convention
    ctx.translate(-this._smoothX, -this._smoothZ);

    // Draw static tiles (crisp pixel art)
    ctx.imageSmoothingEnabled = false;
    if (this._staticCanvas) {
      ctx.drawImage(this._staticCanvas, 0, 0);
    }

    // Dynamic entities (in world-space inside transform)
    this._drawDungeonEntities(ctx, level);

    ctx.restore();

    // Player arrow at center — facing direction relative to camera on screen
    const playerArrowAngle = Math.PI / 2 - this._playerFacing - this._cameraAngle;
    this._drawTriangle(ctx, w / 2, h / 2, playerArrowAngle, '#3b82f6', '#93c5fd', 10);

    // Compass
    this._drawCompass(ctx, w, rotation);
  }

  // ── Overworld rendering ───────────────────────────────────────────

  _renderOverworld(w, h) {
    const ctx = this.ctx;
    const baseRadius = 50;
    const viewRadius = baseRadius / Math.max(this.overworldZoom, 0.01);
    const ppu = Math.min(w, h) / (viewRadius * 2); // pixels per unit
    const rotation = this._cameraAngle + Math.PI;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(5, 10, 20, 0.85)';
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-rotation);
    ctx.scale(-ppu, ppu);  // flip X to match game screen convention
    ctx.translate(-this._smoothX, -this._smoothZ);

    // Cuboids — cull using canvas diagonal (rotation makes corners visible beyond viewRadius)
    const cullRadius = Math.hypot(w, h) / (2 * ppu);
    for (const cuboid of this.world.cuboids.values()) {
      const dx = cuboid.x - this._smoothX;
      const dz = cuboid.z - this._smoothZ;
      const cw = cuboid.width || 0;
      const cd = cuboid.depth || 0;
      // Skip only if cuboid AABB is entirely outside the cull square
      if (dx + cw < -cullRadius || dx > cullRadius || dz + cd < -cullRadius || dz > cullRadius) continue;
      const topMat = cuboid.materials?.[2];
      if (topMat?.color) {
        const c = topMat.color;
        ctx.fillStyle = `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},0.6)`;
      } else {
        ctx.fillStyle = 'rgba(100,120,140,0.5)';
      }
      ctx.fillRect(cuboid.x, cuboid.z, cuboid.width, cuboid.depth);
    }

    // Entity arrows: ea + PI/2 accounts for X-flip transform
    // Sizes divided by ppu so entities appear as fixed screen-pixel sizes
    // Remote players
    if (this.playerSync?.remotePlayers) {
      for (const remote of this.playerSync.remotePlayers.values()) {
        if (!remote.character) continue;
        const ra = ((remote.character.facingAngle || 0) * Math.PI / 180) + Math.PI / 2;
        this._drawTriangle(ctx, remote.character.x, remote.character.z,
          ra, '#22d3ee', '#67e8f9', 10 / ppu);
      }
    }

    // NPCs (show all, not just those with combatStats)
    for (const char of this.world.characters.values()) {
      if (char === this.player || !char.isNPC) continue;
      if (char.combatStats?.isDead) continue;
      const hostile = char.combatStats?.isHostile;
      const na = ((char.facingAngle || 0) * Math.PI / 180) + Math.PI / 2;
      this._drawTriangle(ctx, char.x, char.z, na,
        hostile ? '#ef4444' : '#fbbf24',
        hostile ? '#fca5a5' : '#fde68a',
        10 / ppu);
    }

    ctx.restore();

    // Player arrow — facing direction relative to camera on screen
    const playerArrowAngle = Math.PI / 2 - this._playerFacing - this._cameraAngle;
    this._drawTriangle(ctx, w / 2, h / 2, playerArrowAngle, '#3b82f6', '#93c5fd', 10);
    this._drawCompass(ctx, w, rotation);
  }

  // ── Dynamic dungeon entities ──────────────────────────────────────

  _drawDungeonEntities(ctx, level) {
    // Entity arrows: ea + PI/2 accounts for X-flip transform
    // Camera rotation is handled by the parent ctx.rotate
    // Sizes divided by zoom so entities appear as fixed screen-pixel sizes
    const zoom = this.zoomLevel;

    // NPCs / enemies
    for (const char of this.world.characters.values()) {
      if (char === this.player) continue;
      if (!char.combatStats || char.combatStats.isDead) continue;
      const hostile = char.combatStats.isHostile;
      const a = ((char.facingAngle || 0) * Math.PI / 180) + Math.PI / 2;
      this._drawTriangle(ctx, char.x, char.z, a,
        hostile ? '#ef4444' : '#22c55e',
        hostile ? '#fca5a5' : '#86efac',
        10 / zoom);
    }

    // Remote players
    if (this.playerSync?.remotePlayers) {
      for (const remote of this.playerSync.remotePlayers.values()) {
        if (!remote.character) continue;
        const ra = ((remote.character.facingAngle || 0) * Math.PI / 180) + Math.PI / 2;
        this._drawTriangle(ctx, remote.character.x, remote.character.z,
          ra, '#22d3ee', '#67e8f9', 10 / zoom);
      }
    }

    // Loot items (diamonds)
    if (this.lootManager?.worldItems) {
      ctx.fillStyle = '#fbbf24';
      const lootSize = 6 / zoom;
      const lootHalf = lootSize / 2;
      for (const item of this.lootManager.worldItems.values()) {
        if (item.collected) continue;
        ctx.save();
        ctx.translate(item.x, item.z);
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-lootHalf, -lootHalf, lootSize, lootSize);
        ctx.restore();
      }
    }

    // Exit portal pulse
    if (level.exitPoint) {
      const pulse = 0.5 + 0.5 * Math.sin(Date.now() * 0.003);
      ctx.fillStyle = `rgba(168,85,247,${(0.3 + 0.3 * pulse).toFixed(2)})`;
      ctx.beginPath();
      ctx.arc(level.exitPoint.x, level.exitPoint.z, 0.8 + 0.2 * pulse, 0, Math.PI * 2);
      ctx.fill();
    }

    // Back portal pulse
    if (level.backPortalPoint) {
      const pulse = 0.5 + 0.5 * Math.sin(Date.now() * 0.003 + 1);
      ctx.fillStyle = `rgba(6,182,212,${(0.3 + 0.3 * pulse).toFixed(2)})`;
      ctx.beginPath();
      ctx.arc(level.backPortalPoint.x, level.backPortalPoint.z, 0.8 + 0.2 * pulse, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── Triangle arrow ────────────────────────────────────────────────

  /**
   * Draw a triangle arrow at (cx, cy) pointing in `angle` direction.
   * angle=0 → points up on screen (or up in current transform space).
   */
  _drawTriangle(ctx, cx, cy, angle, fill, stroke, size) {
    const half = size / 2;
    const third = size / 3;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, -half);     // tip
    ctx.lineTo(-third, half); // bottom-left
    ctx.lineTo(third, half);  // bottom-right
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = size * 0.15;
      ctx.stroke();
    }
    ctx.restore();
  }

  // ── Compass ───────────────────────────────────────────────────────

  _drawCompass(ctx, canvasW, rotation) {
    const cx = canvasW - 22;
    const cy = 22;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-rotation);

    // "N" text
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', 0, -8);

    // Small north chevron above N, pointing up and away
    ctx.beginPath();
    ctx.moveTo(0, -18);   // tip (away from center)
    ctx.lineTo(-4, -13);  // base left
    ctx.lineTo(4, -13);   // base right
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  // ── Zoom: mousewheel ──────────────────────────────────────────────

  _handleWheel(e) {
    e.preventDefault();
    e.stopPropagation();
    const delta = -e.deltaY * 0.002;
    if (this.isDungeonMode) {
      this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom,
        this.zoomLevel * (1 + delta)));
    } else {
      this.overworldZoom = Math.max(this.minOverworldZoom, Math.min(this.maxOverworldZoom,
        this.overworldZoom * (1 + delta)));
    }
  }

  // ── Zoom: pinch ───────────────────────────────────────────────────

  _handlePointerDown(e) {
    this._activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this._activeTouches.size === 2) {
      const pts = [...this._activeTouches.values()];
      this._pinchStartDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      this._pinchStartZoom = this.isDungeonMode ? this.zoomLevel : this.overworldZoom;
    }
  }

  _handlePointerMove(e) {
    if (!this._activeTouches.has(e.pointerId)) return;
    this._activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this._activeTouches.size === 2) {
      const pts = [...this._activeTouches.values()];
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      if (this._pinchStartDist > 0) {
        const ratio = dist / this._pinchStartDist;
        const newZoom = this._pinchStartZoom * ratio;
        if (this.isDungeonMode) {
          this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
        } else {
          this.overworldZoom = Math.max(this.minOverworldZoom, Math.min(this.maxOverworldZoom, newZoom));
        }
      }
    }
  }

  _handlePointerUp(e) {
    this._activeTouches.delete(e.pointerId);
  }
}
